/**
 * Path Remapping Validation Utilities
 * 
 * Ensures that external (custom) paths have matching parameter names
 * with internal (plugin) paths to avoid confusion and bugs.
 */

/**
 * Extract parameter names from a path pattern
 * @param pattern - Path pattern like "/hello/:param1/:param2"
 * @returns Array of parameter names like ["param1", "param2"]
 */
export function extractParamNames(pattern: string): string[] {
    const paramRegex = /:([^/]+)/g;
    const matches = [];
    let match;

    while ((match = paramRegex.exec(pattern)) !== null) {
        matches.push(match[1]);
    }

    return matches;
}

/**
 * Validate that two path patterns have matching parameter names
 * 
 * @param internalPath - The internal/original plugin path (e.g., "/hello/:myparam")
 * @param externalPath - The external/custom path (e.g., "/custom/:myparam")
 * @returns Validation result with error message if invalid
 */
export function validatePathRemapping(
    internalPath: string,
    externalPath: string
): { valid: boolean; error?: string; suggestion?: string } {
    // If paths are the same, no remapping needed - valid
    if (internalPath === externalPath) {
        return { valid: true };
    }

    const internalParams = extractParamNames(internalPath);
    const externalParams = extractParamNames(externalPath);

    // Check if parameter counts match
    if (internalParams.length !== externalParams.length) {
        return {
            valid: false,
            error: `Parameter count mismatch: internal path has ${internalParams.length} parameter(s), custom path has ${externalParams.length}`,
            suggestion: internalParams.length > 0
                ? `Custom path should have ${internalParams.length} parameter(s): ${internalParams.map(p => `:${p}`).join(', ')}`
                : 'Custom path should not have any parameters'
        };
    }

    // Check if all parameter names match (in order)
    const mismatchedParams: string[] = [];
    for (let i = 0; i < internalParams.length; i++) {
        if (internalParams[i] !== externalParams[i]) {
            mismatchedParams.push(`position ${i + 1}: expected :${internalParams[i]}, got :${externalParams[i]}`);
        }
    }

    if (mismatchedParams.length > 0) {
        return {
            valid: false,
            error: `Parameter names must match: ${mismatchedParams.join('; ')}`,
            suggestion: `Use the same parameter names as the internal path: ${internalParams.map(p => `:${p}`).join(', ')}`
        };
    }

    // All checks passed
    return { valid: true };
}

/**
 * Generate a suggested custom path based on the internal path
 * Keeps the same parameter names but allows customizing the static parts
 * 
 * @param internalPath - The internal path (e.g., "/hello-with-param/:myparam")
 * @param prefix - Optional prefix to add (e.g., "/custom")
 * @returns Suggested custom path
 */
export function generateSuggestedCustomPath(
    internalPath: string,
    prefix?: string
): string {
    if (!prefix) {
        return internalPath;
    }

    // Extract the path parts
    const parts = internalPath.split('/').filter(Boolean);
    const params = extractParamNames(internalPath);

    if (params.length === 0) {
        // No parameters, simple concatenation
        return `${prefix}${internalPath.startsWith('/') ? '' : '/'}${internalPath}`;
    }

    // Build path preserving parameter names
    const customParts = [prefix];
    
    parts.forEach(part => {
        if (part.startsWith(':')) {
            // Keep parameter as-is
            customParts.push(part);
        } else {
            // You can customize static parts here
            customParts.push(part);
        }
    });

    return '/' + customParts.filter(Boolean).join('/');
}

/**
 * Check if a path has parameters
 */
export function hasParameters(path: string): boolean {
    return extractParamNames(path).length > 0;
}

/**
 * Format parameter names for display
 */
export function formatParameterNames(params: string[]): string {
    if (params.length === 0) return 'none';
    return params.map(p => `:${p}`).join(', ');
}

